/**
 * Isomorphic constants shared by the browser bundle and Cloud Functions.
 * This module must stay dependency-free.
 */

// ---------------------------------------------------------------------------
// Tenancy & roles
// ---------------------------------------------------------------------------

/** Roles that are granted per restaurant, via `restaurants/{id}/members/{uid}`. */
export const RESTAURANT_ROLES = [
  'restaurant_staff',
  'restaurant_manager',
  'restaurant_owner',
] as const
export type RestaurantRole = (typeof RESTAURANT_ROLES)[number]

/** Platform admin is a global capability, not a restaurant membership. */
export const PLATFORM_ADMIN_ROLE = 'platform_admin' as const

export type AppRole = RestaurantRole | typeof PLATFORM_ADMIN_ROLE

/**
 * Numeric ranks make "at least manager" checks trivial and identical in
 * Security Rules, Cloud Functions and the UI.
 */
export const ROLE_RANK: Record<AppRole, number> = {
  restaurant_staff: 1,
  restaurant_manager: 2,
  restaurant_owner: 3,
  platform_admin: 4,
}

export const ROLE_LABELS: Record<AppRole, string> = {
  restaurant_staff: 'Staff',
  restaurant_manager: 'Manager',
  restaurant_owner: 'Owner',
  platform_admin: 'Platform admin',
}

/** Roles an owner is allowed to grant. Owners cannot mint platform admins. */
export const ASSIGNABLE_ROLES: readonly RestaurantRole[] = RESTAURANT_ROLES

export const MEMBER_STATUSES = ['active', 'disabled'] as const
export type MemberStatus = (typeof MEMBER_STATUSES)[number]

// ---------------------------------------------------------------------------
// Bookings
// ---------------------------------------------------------------------------

export const BOOKING_STATUSES = [
  'pending',
  'confirmed',
  'seated',
  'completed',
  'cancelled',
  'no_show',
] as const
export type BookingStatus = (typeof BOOKING_STATUSES)[number]

export const BOOKING_STATUS_LABELS: Record<BookingStatus, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  seated: 'Seated',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_show: 'No-show',
}

/**
 * Statuses that still occupy covers.
 *
 * Only cancellation releases capacity. A no-show is recorded after the fact,
 * when the table has already been held through the sitting — "freeing" those
 * covers would let the diary accept a booking for a time that has passed and
 * would corrupt historical capacity figures.
 */
export const CAPACITY_CONSUMING_STATUSES: readonly BookingStatus[] = [
  'pending',
  'confirmed',
  'seated',
  'completed',
  'no_show',
]

export const RELEASED_STATUSES: readonly BookingStatus[] = ['cancelled']

export const BOOKING_SOURCES = [
  'website',
  'google_organic',
  'google_ads',
  'instagram',
  'facebook',
  'meta_ads',
  'phone',
  'walk_in',
  'returning_customer',
  'recommendation',
  'direct',
  'other',
] as const
export type BookingSource = (typeof BOOKING_SOURCES)[number]

export const BOOKING_SOURCE_LABELS: Record<BookingSource, string> = {
  website: 'Website',
  google_organic: 'Google (organic)',
  google_ads: 'Google Ads',
  instagram: 'Instagram',
  facebook: 'Facebook',
  meta_ads: 'Meta Ads',
  phone: 'Telephone',
  walk_in: 'Walk-in',
  returning_customer: 'Returning customer',
  recommendation: 'Recommendation',
  direct: 'Direct',
  other: 'Other',
}

// ---------------------------------------------------------------------------
// Occasions
// ---------------------------------------------------------------------------

/**
 * Why the table is booked.
 *
 * A CLOSED LIST rather than free text, on purpose. The guest can already write
 * anything they like in the special request; what this adds is something the
 * kitchen and the host stand can act on at a glance — a candle, a card, a
 * quieter table — without reading a paragraph mid-service. A closed list is
 * also the only version that can carry an icon, be counted in reporting, and
 * be translated later.
 *
 * Deliberately short. Every option added here is another thing to scan on a
 * booking form, and the free-text field remains for everything else.
 */
export const BOOKING_OCCASIONS = [
  'birthday',
  'anniversary',
  'engagement',
  'celebration',
  'date_night',
  'business',
] as const
export type BookingOccasion = (typeof BOOKING_OCCASIONS)[number]

export const BOOKING_OCCASION_LABELS: Record<BookingOccasion, string> = {
  birthday: 'Birthday',
  anniversary: 'Anniversary',
  engagement: 'Engagement',
  celebration: 'Celebration',
  date_night: 'Date night',
  business: 'Business meal',
}

/** Sources a member of staff may pick when logging an offline booking. */
export const MANUAL_BOOKING_SOURCES: readonly BookingSource[] = [
  'phone',
  'walk_in',
  'instagram',
  'facebook',
  'google_organic',
  'returning_customer',
  'recommendation',
  'other',
]

export const CREATED_BY = ['customer', 'staff', 'system'] as const
export type CreatedBy = (typeof CREATED_BY)[number]

export const CANCELLATION_SOURCES = ['customer', 'staff', 'system'] as const
export type CancellationSource = (typeof CANCELLATION_SOURCES)[number]

export const REMINDER_STATES = ['pending', 'sent', 'skipped'] as const
export type ReminderState = (typeof REMINDER_STATES)[number]

// ---------------------------------------------------------------------------
// Capacity model
// ---------------------------------------------------------------------------

/**
 * Internal capacity granularity. Customer-facing slots are coarser
 * (`service.slotIntervalMinutes`, typically 30) but capacity is always
 * accounted in 15-minute blocks so that a 19:15 walk-in and a 19:30 booking
 * cannot silently share the same covers.
 *
 * A booking occupies the half-open interval [startAt, endAt) — a booking that
 * ends at 20:30 does NOT consume the 20:30 block.
 */
export const CAPACITY_BLOCK_MINUTES = 15
export const MINUTES_PER_DAY = 1440
/** Guard rail: a single booking may never span more than 12 hours. */
export const MAX_BOOKING_DURATION_MINUTES = 720

// ---------------------------------------------------------------------------
// Locale defaults
// ---------------------------------------------------------------------------

export const DEFAULT_TIMEZONE = 'Europe/London'
export const DEFAULT_CURRENCY = 'GBP'
export const DEFAULT_LOCALE = 'en-GB'

export const WEEKDAYS = [
  { value: 1, short: 'Mon', label: 'Monday' },
  { value: 2, short: 'Tue', label: 'Tuesday' },
  { value: 3, short: 'Wed', label: 'Wednesday' },
  { value: 4, short: 'Thu', label: 'Thursday' },
  { value: 5, short: 'Fri', label: 'Friday' },
  { value: 6, short: 'Sat', label: 'Saturday' },
  { value: 7, short: 'Sun', label: 'Sunday' },
] as const

// ---------------------------------------------------------------------------
// Closures / overrides
// ---------------------------------------------------------------------------

export const CLOSURE_TYPES = [
  'closed_day',
  'closed_range',
  'special_hours',
  'capacity_override',
  'blocked_times',
] as const
export type ClosureType = (typeof CLOSURE_TYPES)[number]

export const CLOSURE_TYPE_LABELS: Record<ClosureType, string> = {
  closed_day: 'Closed all day',
  closed_range: 'Closed date range',
  special_hours: 'One-off opening hours',
  capacity_override: 'Capacity override',
  blocked_times: 'Blocked time range',
}

// ---------------------------------------------------------------------------
// Campaigns
// ---------------------------------------------------------------------------

export const CAMPAIGN_PLATFORMS = [
  'google_ads',
  'meta_ads',
  'instagram_organic',
  'facebook_organic',
  'google_business_profile',
  'email',
  'influencer',
  'qr_code',
  'print',
  'restaurant_website',
  'partner_website',
  'direct',
  'other',
] as const
export type CampaignPlatform = (typeof CAMPAIGN_PLATFORMS)[number]

export const CAMPAIGN_PLATFORM_LABELS: Record<CampaignPlatform, string> = {
  google_ads: 'Google Ads',
  meta_ads: 'Meta Ads',
  instagram_organic: 'Instagram (organic)',
  facebook_organic: 'Facebook (organic)',
  google_business_profile: 'Google Business Profile',
  email: 'Email campaign',
  influencer: 'Influencer',
  qr_code: 'QR code',
  print: 'Printed flyer',
  restaurant_website: 'Restaurant website',
  partner_website: 'Partner website',
  direct: 'Direct campaign',
  other: 'Other',
}

/**
 * Sensible defaults so a campaign always produces coherent attribution even if
 * the user leaves source/medium untouched.
 */
export const CAMPAIGN_PLATFORM_DEFAULTS: Record<
  CampaignPlatform,
  { source: string; medium: string; bookingSource: BookingSource }
> = {
  google_ads: { source: 'google', medium: 'cpc', bookingSource: 'google_ads' },
  meta_ads: { source: 'meta', medium: 'paid_social', bookingSource: 'meta_ads' },
  instagram_organic: { source: 'instagram', medium: 'social', bookingSource: 'instagram' },
  facebook_organic: { source: 'facebook', medium: 'social', bookingSource: 'facebook' },
  google_business_profile: { source: 'google', medium: 'organic', bookingSource: 'google_organic' },
  email: { source: 'email', medium: 'email', bookingSource: 'other' },
  influencer: { source: 'influencer', medium: 'referral', bookingSource: 'recommendation' },
  qr_code: { source: 'qr', medium: 'offline', bookingSource: 'other' },
  print: { source: 'print', medium: 'offline', bookingSource: 'other' },
  restaurant_website: { source: 'website', medium: 'owned', bookingSource: 'website' },
  partner_website: { source: 'partner', medium: 'referral', bookingSource: 'other' },
  direct: { source: 'direct', medium: 'none', bookingSource: 'direct' },
  other: { source: 'other', medium: 'other', bookingSource: 'other' },
}

export const CAMPAIGN_STATUSES = ['active', 'paused', 'archived'] as const
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number]

/** Public campaign identifiers look like `cmp_7KD42MQ9`. */
export const CAMPAIGN_PUBLIC_ID_PREFIX = 'cmp_'
export const CAMPAIGN_PUBLIC_ID_LENGTH = 8

// ---------------------------------------------------------------------------
// Attribution
// ---------------------------------------------------------------------------

export const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const

export const CLICK_ID_PARAMS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid'] as const
export type ClickIdParam = (typeof CLICK_ID_PARAMS)[number]

/** The only query parameters we will ever read from a booking URL. */
export const ALLOWED_ATTRIBUTION_PARAMS: readonly string[] = [...UTM_PARAMS, ...CLICK_ID_PARAMS]

export const ATTRIBUTION_SESSION_KEY = 'coverbook.attribution.v1'
export const CONSENT_SESSION_KEY = 'coverbook.consent.v1'
export const FUNNEL_SESSION_KEY = 'coverbook.funnel.v1'

// ---------------------------------------------------------------------------
// Analytics / funnel
// ---------------------------------------------------------------------------

export const FUNNEL_EVENTS = [
  'booking_widget_open',
  'availability_search',
  'available_slots_viewed',
  'booking_slot_selected',
  'booking_form_started',
  'booking_submitted',
  'booking_confirmed',
  'booking_cancelled',
] as const
export type FunnelEvent = (typeof FUNNEL_EVENTS)[number]

/**
 * Funnel events a browser is allowed to report directly. `booking_confirmed`
 * and `booking_cancelled` are counted server-side from the authoritative
 * booking write, so accepting them from a client would let anyone inflate
 * conversions.
 */
export const CLIENT_REPORTABLE_FUNNEL_EVENTS: readonly FunnelEvent[] = [
  'booking_widget_open',
  'availability_search',
  'available_slots_viewed',
  'booking_slot_selected',
  'booking_form_started',
  'booking_submitted',
]

export const GA4_EVENT_NAMES: Record<FunnelEvent, string> = {
  booking_widget_open: 'booking_widget_open',
  availability_search: 'availability_search',
  available_slots_viewed: 'available_slots_viewed',
  booking_slot_selected: 'booking_slot_selected',
  booking_form_started: 'begin_checkout',
  booking_submitted: 'booking_submitted',
  booking_confirmed: 'purchase',
  booking_cancelled: 'refund',
}

// ---------------------------------------------------------------------------
// Widget <-> parent page contract
// ---------------------------------------------------------------------------

/** Envelope identifier. Documented in docs/WIDGET.md — do not change lightly. */
export const WIDGET_MESSAGE_SOURCE = 'restaurant-booking-widget' as const
export const WIDGET_MESSAGE_VERSION = 1 as const

/**
 * A SEPARATE envelope for the dashboard's own settings preview.
 *
 * Deliberately not the public widget envelope: that one is origin-checked
 * against the restaurant's configured list and is part of the contract host
 * pages integrate against. This one is same-origin only and internal, so it
 * must never be confused with a message a restaurant's website could send.
 *
 * It lives here rather than beside the booking page because the settings screen
 * needs it too, and importing it from there would pull the whole public booking
 * page into the dashboard bundle — the routes are lazily split precisely so
 * that cannot happen.
 */
export const WIDGET_PREVIEW_SOURCE = 'coverbook-preview' as const

export const WIDGET_OUTBOUND_EVENTS = [
  'widget_ready',
  'widget_resize',
  'widget_close',
  ...FUNNEL_EVENTS,
] as const
export type WidgetOutboundEvent = (typeof WIDGET_OUTBOUND_EVENTS)[number]

export const WIDGET_INBOUND_EVENTS = ['set_consent', 'host_info', 'close_request'] as const
export type WidgetInboundEvent = (typeof WIDGET_INBOUND_EVENTS)[number]

export const WIDGET_THEMES = ['light', 'dark'] as const
export type WidgetTheme = (typeof WIDGET_THEMES)[number]

export const WIDGET_MODES = ['inline', 'modal'] as const
export type WidgetMode = (typeof WIDGET_MODES)[number]

/** Safe, self-hostable font stacks. No third-party font requests, ever. */
export const WIDGET_FONTS = ['system', 'serif', 'humanist', 'geometric', 'mono'] as const
export type WidgetFont = (typeof WIDGET_FONTS)[number]

export const WIDGET_FONT_STACKS: Record<WidgetFont, string> = {
  system:
    'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  serif: 'ui-serif, Georgia, Cambria, "Times New Roman", Times, serif',
  humanist: '"Optima", "Segoe UI", Candara, "Gill Sans", "Trebuchet MS", sans-serif',
  geometric: '"Century Gothic", "Avenir Next", Futura, "Trebuchet MS", sans-serif',
  mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
}

// ---------------------------------------------------------------------------
// Consent
// ---------------------------------------------------------------------------

export const CONSENT_SOURCES = ['widget', 'website', 'admin'] as const
export type ConsentSource = (typeof CONSENT_SOURCES)[number]

/**
 * Bump when the customer-facing marketing wording changes materially, so
 * historical consent records remain interpretable.
 */
export const DEFAULT_CONSENT_TEXT_VERSION = '2026-01-v1'

export const DEFAULT_MARKETING_CONSENT_TEXT =
  'I would like to receive news, offers and event updates from this restaurant.'

// ---------------------------------------------------------------------------
// Email
// ---------------------------------------------------------------------------

export const EMAIL_TEMPLATES = [
  'booking_received',
  'booking_confirmed',
  'booking_changed',
  'booking_cancelled',
  'booking_reminder',
  'restaurant_new_booking',
  'restaurant_cancelled_booking',
  'booking_change_requested',
  'member_invite',
  'account_welcome',
] as const
export type EmailTemplate = (typeof EMAIL_TEMPLATES)[number]

export const EMAIL_STATUSES = ['pending', 'sending', 'sent', 'failed', 'cancelled'] as const
export type EmailStatus = (typeof EMAIL_STATUSES)[number]

export const EMAIL_MAX_ATTEMPTS = 5

// ---------------------------------------------------------------------------
// Booking reference
// ---------------------------------------------------------------------------

/**
 * Crockford-ish alphabet: no 0/O, no 1/I/L, no U. Easy to read aloud over a
 * noisy dining room telephone.
 */
export const REFERENCE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTVWXYZ'
export const REFERENCE_BODY_LENGTH = 5
export const REFERENCE_PREFIX_LENGTH = 3

// ---------------------------------------------------------------------------
// Audit
// ---------------------------------------------------------------------------

export const AUDIT_ENTITY_TYPES = [
  'booking',
  'restaurant',
  'service',
  'closure',
  'campaign',
  'member',
  'guest',
  'export',
  'security',
  /** Billing tenant: trial extensions, plan and status changes. */
  'account',
] as const
export type AuditEntityType = (typeof AUDIT_ENTITY_TYPES)[number]

export const AUDIT_ACTOR_TYPES = ['user', 'customer', 'system'] as const
export type AuditActorType = (typeof AUDIT_ACTOR_TYPES)[number]

/**
 * Never copied into an audit document. Audit logs record *that* a change
 * happened and by whom, not the customer's personal data.
 */
export const AUDIT_REDACTED_FIELDS: readonly string[] = [
  'customer',
  'email',
  // Plural too: `notifications.emails` is a list of staff addresses and has no
  // business being diffed into an audit record that every manager can read.
  'emails',
  'notifications',
  'to',
  'replyTo',
  'phone',
  'fullName',
  'specialRequest',
  'internalNote',
  'search',
  'tokenHash',
  'attribution',
]

// ---------------------------------------------------------------------------
// Input limits (enforced by Zod on both sides)
// ---------------------------------------------------------------------------

export const LIMITS = {
  fullName: 120,
  email: 254,
  phone: 32,
  specialRequest: 500,
  internalNote: 1000,
  cancellationReason: 300,
  restaurantName: 120,
  slug: 60,
  campaignName: 80,
  campaignLabel: 80,
  utmValue: 100,
  headline: 120,
  buttonLabel: 40,
  bookingMessage: 300,
  confirmationMessage: 300,
  cancellationPolicy: 2000,
  marketingConsentText: 500,
  url: 500,
  addressLine: 120,
  origin: 253,
  notificationEmails: 10,
  allowedOrigins: 20,
  exportRows: 5000,
  searchTerm: 100,
} as const

export const PARTY_SIZE_ABSOLUTE_MIN = 1
export const PARTY_SIZE_ABSOLUTE_MAX = 50

// ---------------------------------------------------------------------------
// Rate limits (per rolling window, enforced in Cloud Functions)
// ---------------------------------------------------------------------------

export const RATE_LIMITS = {
  /**
   * Deliberately generous. Buckets are keyed by (caller, restaurant, window),
   * and a great many real diners share one apparent address behind mobile
   * carrier NAT, an office gateway or a hotel network. A tight per-IP limit
   * would turn a busy Friday into a wall of refusals for legitimate guests.
   *
   * The primary defence against scripted abuse is App Check; this bound exists
   * to stop a single source hammering the booking endpoint, not to police
   * ordinary demand.
   */
  createPublicBooking: { limit: 20, windowSeconds: 600 },
  searchAvailability: { limit: 120, windowSeconds: 600 },
  getBookingContext: { limit: 120, windowSeconds: 600 },
  recordFunnelEvent: { limit: 300, windowSeconds: 600 },
  cancelCustomerBooking: { limit: 20, windowSeconds: 600 },
  requestBookingChange: { limit: 5, windowSeconds: 3600 },
  exportBookings: { limit: 20, windowSeconds: 3600 },
} as const
export type RateLimitedAction = keyof typeof RATE_LIMITS

// ---------------------------------------------------------------------------
// Booking management tokens
// ---------------------------------------------------------------------------

/** 32 random bytes, base64url encoded. Only the HMAC is ever persisted. */
export const MANAGEMENT_TOKEN_BYTES = 32
/** Link stays valid until 30 days after the booking, then it is useless. */
export const MANAGEMENT_TOKEN_TTL_DAYS_AFTER_BOOKING = 30

// ---------------------------------------------------------------------------
// Reminders
// ---------------------------------------------------------------------------

export const REMINDER_LEAD_HOURS = 24
/** Do not send a reminder for a booking made inside this window. */
export const REMINDER_MIN_LEAD_HOURS = 3
